import mirror from 'keymirror';
import { gql } from '@apollo/client';
import { trackEvent, EventNames, getAlgoliaTracking } from '../lib/analytics';
import { getLocale, getTimezone } from '../utils/date-utils';
import getClient from '../core/apollo/playqlClient';

export const actionTypes = mirror({
  GET_FAVORITES_ERROR: 'GET_FAVORITES_ERROR',
  GET_FAVORITES_LOADING: 'GET_FAVORITES_LOADING',
  GET_FAVORITES_SUCCESS: 'GET_FAVORITES_SUCCESS',
  SET_FAVORITE_ERROR: 'SET_FAVORITE_ERROR',
  SET_FAVORITE_SUCCESS: 'SET_FAVORITE_SUCCESS',
  SET_FAVORITE_FROM_FAVORITE: 'SET_FAVORITE_FROM_FAVORITE',
  SET_FAVORITES_ALERT: 'SET_FAVORITES_ALERT',
  SET_FAVORITES_FILTERS: 'SET_FAVORITES_FILTERS',
  UNSET_FAVORITE_ERROR: 'UNSET_FAVORITE_ERROR',
  UNSET_FAVORITE_SUCCESS: 'UNSET_FAVORITE_SUCCESS',
});

const getTrackingProperties = (category, id, data) => {
  if (category === 'lesson')
    return {
      lesson_id: id ?? '',
      lesson_slug: data?.slug,
      lesson_type_1: data?.type,
      lesson_name: data?.name,
    };
  if (category === 'course') {
    return {
      course_id: id ?? '',
      course_slug: data?.slug,
      course_name: data?.name,
    };
  }
  return {
    practice_sheet_id: id,
  };
};

const SET_FAVORITE = gql`
  mutation SetFavorite($setFavoriteInput: SetFavoriteInput!) {
    setFavorite(input: $setFavoriteInput) {
      artist
      category
      dateAdded
      duration
      id
      image
      instrument
      numActivities
      slug
      title
      type
    }
  }
`;

const UNSET_FAVORITE = gql`
  mutation UnsetFavorite($unsetFavoriteInput: String!) {
    unsetFavorite(id: $unsetFavoriteInput)
  }
`;

export const GET_FAVORITES = gql`
  query {
    favorites {
      artist
      category
      dateAdded
      duration
      id
      image
      instrument
      numActivities
      slug
      title
      type
    }
  }
`;

const setFavoriteError = error => ({
  type: actionTypes.SET_FAVORITE_ERROR,
  error,
});

const setFavoriteSuccess = (data, keys) => ({
  type: actionTypes.SET_FAVORITE_SUCCESS,
  data,
  keys,
});

const setFavoriteFromFavorite = keys => ({
  type: actionTypes.SET_FAVORITE_FROM_FAVORITE,
  keys,
});

const setFavoritesAlert = alert => ({
  type: actionTypes.SET_FAVORITES_ALERT,
  alert,
});

const unsetFavoriteError = error => ({
  type: actionTypes.UNSET_FAVORITE_ERROR,
  error,
});

const unsetFavoriteSuccess = (data, keys) => ({
  type: actionTypes.UNSET_FAVORITE_SUCCESS,
  data,
  keys,
});

const setFavoritesFilters = filters => ({
  type: actionTypes.SET_FAVORITES_FILTERS,
  filters,
});

const getFavoritesLoading = () => ({
  type: actionTypes.GET_FAVORITES_LOADING,
  loading: true,
});

const getFavoritesSuccess = (data, keys) => ({
  type: actionTypes.GET_FAVORITES_SUCCESS,
  data,
  keys,
});

const getFavoritesError = error => ({
  type: actionTypes.GET_FAVORITES_ERROR,
  error,
});

const setFavoriteAction = (id, category, client, sourcePath) => {
  return async (dispatch, getState) => {
    try {
      const { favorites } = getState();
      const { data, keys } = favorites;
      const options = {
        mutation: SET_FAVORITE,
        variables: {
          setFavoriteInput: { id, category },
        },
      };
      const { data: response } = await client.mutate(options);
      if (!response || !response.setFavorite) {
        const error = 'Error setting favorite: invalid Apollo response';
        dispatch(setFavoriteError(error));
        throw new Error(error);
      }
      if (sourcePath !== actionTypes.FAVORITES_PAGE) {
        dispatch(
          setFavoriteSuccess([{ ...response.setFavorite }, ...data], {
            ...keys,
            [id]: true,
          }),
        );
      } else {
        dispatch(setFavoriteFromFavorite({ ...keys, [id]: true }));
      }
      dispatch(
        setFavoritesAlert({
          didSet: true,
          isVisible: true,
        }),
      );
      setTimeout(() => {
        dispatch(
          setFavoritesAlert({
            didSet: true,
            isVisible: false,
          }),
        );
      }, 4000);
    } catch (error) {
      dispatch(setFavoriteError(error));
      throw new Error(error);
    }
  };
};

export function setFavorite(id, category, sourcePath, favoriteData = {}, userID) {
  return async (dispatch, getState) => {
    const state = getState();
    const client = await getClient();
    dispatch(setFavoriteAction(id, category, client, sourcePath));

    const globalProperties = {
      fc_id: userID,
    };

    const legacyProperties = {
      timezone: getTimezone(),
      locale: getLocale(),
      source_path: sourcePath,
    };

    trackEvent(
      EventNames.FAVORITE_ADDED,
      {
        ...globalProperties,
        ...getTrackingProperties(category, id, favoriteData),
        ...getAlgoliaTracking('conversion', id),
      },
      legacyProperties,
    );
  };
}

const unsetFavoriteAction = (id, client, sourcePath) => {
  return async (dispatch, getState) => {
    try {
      const { favorites } = await getState();
      const { data: prevData, keys: prevKeys } = favorites;
      const options = {
        mutation: UNSET_FAVORITE,
        variables: {
          unsetFavoriteInput: id,
        },
      };
      const { data: response } = await client.mutate(options);
      if ((response && !response.unsetFavorite) || !response) {
        const error = 'Error unsetting favorite: invalid Apollo response';
        dispatch(unsetFavoriteError(error));
        throw new Error(error);
      }
      const prevFavoriteIndex = prevData.findIndex(favorite => favorite.id === id);
      const data = [...prevData];
      const keys = { ...prevKeys };
      if (sourcePath !== sourcePath.FAVORITES_PAGE) {
        data.splice(prevFavoriteIndex, 1);
      }
      delete keys[id];
      dispatch(unsetFavoriteSuccess(data, keys));
      dispatch(
        setFavoritesAlert({
          didSet: false,
          isVisible: true,
        }),
      );
      setTimeout(() => {
        dispatch(
          setFavoritesAlert({
            didSet: false,
            isVisible: false,
          }),
        );
      }, 4000);
    } catch (error) {
      dispatch(unsetFavoriteError(error));
      throw new Error(error);
    }
  };
};

export function unsetFavorite(id, category, sourcePath, favoriteData = {}, userID) {
  return async (dispatch, getState) => {
    const state = getState();
    const client = await getClient();
    dispatch(unsetFavoriteAction(id, client, sourcePath));

    const globalProperties = {
      fc_id: userID,
    };

    const legacyProperties = {
      timezone: getTimezone(),
      locale: getLocale(),
      source_path: sourcePath,
    };

    trackEvent(
      EventNames.FAVORITE_REMOVED,
      {
        ...globalProperties,
        ...getTrackingProperties(category, id, favoriteData),
      },
      legacyProperties,
    );
  };
}

const pruneFavoritesAction = () => {
  return async (dispatch, getState) => {
    const { favorites } = await getState();
    const data = [...favorites.data].filter(favorite => favorites.keys[favorite.id]);
    dispatch(setFavoriteSuccess([...data], { ...favorites.keys }));
  };
};

export function pruneFavorites() {
  return async dispatch => {
    dispatch(pruneFavoritesAction());
  };
}

const setFiltersAction = filters => {
  return async dispatch => {
    dispatch(setFavoritesFilters(filters));
  };
};

export function setFilters(filters) {
  return async dispatch => {
    dispatch(setFiltersAction(filters));
  };
}

const getFavoritesAction = client => {
  return async dispatch => {
    dispatch(getFavoritesLoading());
    try {
      const options = {
        query: GET_FAVORITES,
      };
      const {
        data: { favorites: data },
      } = await client.query(options);
      const keys = data.reduce((obj, item) => Object.assign(obj, { [item.id]: true }), {});
      dispatch(getFavoritesSuccess(data, keys));
    } catch (error) {
      dispatch(getFavoritesError(error));
    }
  };
};

export function getFavorites() {
  return async dispatch => {
    const client = await getClient();
    dispatch(getFavoritesAction(client));
  };
}
